"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useForm = void 0;
const react_1 = require("react");
const reactiveState_1 = require("../reactiveState");
const logic_1 = require("./logic");
const utils_1 = require("./utils");
const constants_1 = require("./constants");
const useCallbacks = ({ mode, state, setState, validationSchema, initialValues, resetFormOnSubmit, }) => {
    const setValues = (0, react_1.useCallback)((values) => {
        setState({ values });
    }, []);
    const resetForm = (0, react_1.useCallback)(() => {
        (0, reactiveState_1.runReaction)(() => {
            state.dirtyFields = {};
            state.touchedFields = {};
            state.schemaErrors = {};
            state.values = initialValues.current;
            setState({ meta: (0, logic_1.getResetForm)() });
        });
    }, []);
    const handleBlur = (event) => {
        const { name, value, type, checked } = event.target;
        const newValue = type === 'checkbox' ? checked : value;
        const nextState = { touchedFields: { [name]: true } };
        if (mode === 'all' || mode === 'onBlur') {
            nextState.schemaErrors = { [name]: (0, utils_1.validate)(validationSchema[name], newValue, name, state.values) };
        }
        setState(nextState);
    };
    const handleValidateField = (name) => {
        const nextState = {};
        const error = (0, utils_1.validate)(validationSchema[name], state.values[name], name, state.values);
        nextState.schemaErrors = { [name]: error };
        setState(nextState);
        return error;
    };
    const handleSubmitFormReset = () => {
        resetFormOnSubmit &&
            (0, reactiveState_1.runReaction)(() => {
                state.schemaErrors = {};
                state.dirtyFields = {};
                state.touchedFields = {};
                state.values = initialValues.current;
                setState({ meta: (0, logic_1.getResetForm)() });
            });
    };
    const handleSubmit = (submitHandler) => async (event) => {
        event.preventDefault();
        const nextState = {
            meta: {
                isSubmitting: true,
                isSubmitted: true,
                submitCount: state.meta.submitCount + 1,
            },
        };
        if (mode === 'all' || mode === 'onSubmit') {
            nextState.schemaErrors = (0, logic_1.schemaErrorLookUp)(state.values, validationSchema);
        }
        if (!(0, logic_1.getIsValidForm)(state.internalErrors, nextState.schemaErrors || state.schemaErrors)) {
            nextState.meta.isSubmitting = false;
            setState(nextState);
            return;
        }
        try {
            await submitHandler();
            handleSubmitFormReset();
        }
        catch {
            nextState.meta.isSubmitting = false;
            setState(nextState);
        }
    };
    const handleChange = (event, error) => {
        const { name, value: eventValue, type, checked } = event.target;
        const newValue = type === 'checkbox' && !checked ? null : eventValue;
        const isDirtyField = newValue !== initialValues.current[name];
        const isDirtyForm = (0, utils_1.someDirtyField)(state.dirtyFields, [name]);
        (0, reactiveState_1.runReaction)(() => {
            setState({
                values: { [name]: newValue },
                internalErrors: { [name]: error?.errorsFromField },
                dirtyFields: { [name]: isDirtyField },
                meta: {
                    isDirty: isDirtyField || isDirtyForm,
                },
            });
        });
    };
    return { resetForm, handleBlur, handleChange, handleValidateField, setValues, handleSubmit };
};
const useForm = (defaultValues, { mode = constants_1.VALIDATION_MODE.default, validationSchema = {}, resetFormOnSubmit = true, } = {
    mode: constants_1.VALIDATION_MODE.default,
}) => {
    const initialValues = (0, react_1.useRef)(((0, utils_1.isDefaultValues)(defaultValues) ? (0, utils_1.flattenType)(defaultValues) : {}));
    const [stateObservable] = (0, react_1.useState)(() => (0, reactiveState_1.createObservable)({
        values: initialValues.current,
        dirtyFields: {},
        touchedFields: {},
        schemaErrors: {},
        internalErrors: {},
        meta: {
            submitCount: 0,
            isDirty: false,
            isSubmitted: false,
            isSubmitting: false,
            isValid: false,
            isLoading: !(0, utils_1.isDefaultValues)(defaultValues),
        },
    }));
    const [state, setState] = (0, reactiveState_1.useReactiveState)(stateObservable);
    const { resetForm, setValues, handleBlur, handleChange, handleSubmit, handleValidateField } = useCallbacks({
        state,
        setState,
        mode,
        validationSchema,
        initialValues,
        resetFormOnSubmit,
    });
    const control = {
        resetForm,
        setValues,
        validateField: handleValidateField,
        onChange: handleChange,
        onSubmit: handleSubmit,
        onBlur: handleBlur,
    };
    const Form = (0, react_1.useRef)();
    const Field = (0, react_1.useRef)();
    if (!Field.current) {
        Field.current = (0, logic_1.getField)({ state: stateObservable, onChange: handleChange, onBlur: handleBlur });
    }
    if (!Form.current) {
        Form.current = (0, logic_1.getForm)({ control, state: stateObservable, Field: Field.current });
    }
    (0, react_1.useEffect)(() => {
        state.schemaErrors = Object.keys(validationSchema).reduce((acc, key) => {
            acc[key] = undefined;
            return acc;
        }, {});
    }, []);
    (0, react_1.useEffect)(() => {
        if (mode === 'onBlur') {
            Object.entries(validationSchema).forEach(([key, validation]) => {
                const error = (0, utils_1.validate)(validation, state.values[key], key, state.values);
                setState({ schemaErrors: { [key]: error } });
            });
        }
        const shouldValidateOnChange = mode === constants_1.VALIDATION_MODE.default || mode === constants_1.VALIDATION_MODE.all || mode === constants_1.VALIDATION_MODE.onChange;
        if (!shouldValidateOnChange) {
            return;
        }
        const disposes = Object.entries(validationSchema).map(([key, validation]) => {
            return (0, reactiveState_1.autorun)(() => {
                const error = (0, utils_1.validate)(validation, state.values[key], key, state.values);
                const schemaError = () => {
                    if (mode === constants_1.VALIDATION_MODE.default && !state.touchedFields[key]) {
                        return undefined;
                    }
                    return error;
                };
                setState({ schemaErrors: { [key]: schemaError }, internalErrors: { [key]: error } });
            });
        });
        return () => disposes.forEach(dispose => dispose());
    }, [validationSchema, mode]);
    (0, reactiveState_1.useReaction)(() => {
        state.meta.isValid = (0, logic_1.getIsValidForm)(state.internalErrors, state.schemaErrors);
    });
    (0, react_1.useEffect)(() => {
        const fetchDefaultValues = async () => {
            const promise = defaultValues;
            const response = await promise();
            initialValues.current = (0, utils_1.flattenType)(response);
            state.values = initialValues.current;
            state.meta.isLoading = false;
        };
        if (!(0, utils_1.isDefaultValues)(defaultValues)) {
            fetchDefaultValues();
        }
    }, []);
    return {
        Field: Field.current,
        Form: Form.current,
        state,
        control,
    };
};
exports.useForm = useForm;
//# sourceMappingURL=useForm.js.map